<template>
  <div class="platform-import-page">
    <div class="settings-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a href="#/settings?tabIndex=4" class="btn btn-orange btn-icon back" :class="{ disabled: busy }" :title="$t('general.back')" :aria-disabled="busy ? 'true' : 'false'" @click.prevent="goBack" @keydown.space.prevent="goBack">
            <i class="ti-arrow-left" aria-hidden="true"></i> {{ $t('general.back') }}
          </a>
        </p>
      </div>
    </div>
    <div class="platform-import">
    <header class="import-header">
      <div>
        <h4>{{ t('title') }}</h4>
        <p>{{ t('intro') }}</p>
      </div>
    </header>
    <form class="url-form" @submit.prevent="createImport">
      <input v-model.trim="storeUrl" type="url" required placeholder="https://wolt.com/… / https://glovoapp.com/… / https://food.bolt.eu/…" class="form-control" :disabled="busy" :aria-label="t('storeUrl')" @invalid="validateStoreUrl($event.target)" @input="$event.target.setCustomValidity('')">
      <button type="submit" class="import-button import-button--primary" :disabled="busy || !storeUrl.trim()"><i class="fa" :class="busy && activeAction === 'preview' ? 'fa-spinner fa-spin' : 'fa-download'" aria-hidden="true"></i> {{ busy && activeAction === 'preview' ? t('loading') : t('preview') }}</button>
    </form>
    <p v-if="error && !draft" class="import-error" role="alert">{{ error }}</p>
    <p v-if="busy && activeAction === 'preview'" class="import-progress" role="status">{{ t('processing') }}</p>
    <div v-if="batch" class="import-workspace" :aria-busy="busy ? 'true' : 'false'">
      <div class="import-summary"><span class="import-status">{{ batch.platform }} · #{{ batch.id }}</span><span>{{ batch.status }}</span></div>
      <p v-if="batch.error && !error" class="import-error">{{ batch.error }}</p>
      <p v-if="batch.status === 'PRODUCTS_DELETED'" class="import-progress" role="status">{{ t('productsDeleted') }}</p>
      <p v-if="batch.status === 'CONFIRMED'" class="import-success" role="status">{{ t('done') }} · {{ resultCount }} {{ t('products') }}</p>
      <div v-if="draft">
        <details v-if="diagnostics.length" class="diagnostics">
          <summary><i class="fa fa-info-circle" aria-hidden="true"></i> {{ t('gaps') }} ({{ diagnostics.length }})</summary>
          <ul><li v-for="(issue, i) in diagnostics" :key="i">{{ warningMessage(issue) }}</li></ul>
        </details>
        <div class="review-bar">
          <div class="review-tabs" role="group" :aria-label="t('view')">
            <button type="button" :class="{ 'is-active': reviewMode === 'preview' }" :aria-pressed="reviewMode === 'preview'" @click="reviewMode = 'preview'">{{ t('menuPreview') }}</button>
            <button type="button" :class="{ 'is-active': reviewMode === 'edit' }" :aria-pressed="reviewMode === 'edit'" @click="reviewMode = 'edit'">{{ t('editDetails') }}</button>
          </div>
          <input v-model="search" type="search" :placeholder="t('search')" :aria-label="t('search')" class="form-control">
          <span class="review-count">{{ selectedCount }} / {{ draft.products.length }} {{ t('products') }} · {{ draft.currency }}</span>
        </div>
        <div v-if="reviewMode === 'preview'" class="preview-stage">
          <component :is="previewLayout" v-if="previewCategories.length" :categories="previewCategories" :currency="draft.currency" />
          <p v-else class="preview-empty">{{ t('noResults') }}</p>
        </div>
          <div v-show="reviewMode === 'edit'" class="review-columns">
            <aside>
              <h5>{{ t('categories') }}</h5>
              <div v-for="category in draft.categories" :key="category.id" class="category-row">
                <label><input v-model="category.included" type="checkbox" :disabled="!editable || busy" @change="categoryChanged(category)"> {{ category.name }}</label>
                <img v-if="category.image_urls[0]" :src="category.image_urls[0]" alt="" loading="lazy" referrerpolicy="no-referrer">
                <input v-model="category.name" class="form-control" :disabled="!editable || busy">
                <input :value="category.image_urls[0] || ''" class="form-control" :placeholder="t('image')" :disabled="!editable || busy" @change="setImage(category, $event.target.value)">
              </div>
            </aside>
            <main>
              <details v-for="product in filteredProducts" :key="product.id" class="product-row">
                <summary><input v-model="product.included" type="checkbox" :disabled="!editable || busy" @click.stop @change="productChanged(product)"> {{ product.name }} — {{ product.price }} {{ draft.currency }}</summary>
                <div class="product-editor">
                  <img v-if="product.image_urls[0]" :src="product.image_urls[0]" alt="" loading="lazy" referrerpolicy="no-referrer">
                  <label>{{ t('name') }}<input v-model="product.name" class="form-control" :disabled="!editable || busy"></label>
                  <label>{{ t('price') }}<input v-model.number="product.price" type="number" min="0" step="0.01" class="form-control" :disabled="!editable || busy"></label>
                  <label>{{ t('description') }}<textarea v-model="product.description" class="form-control" :disabled="!editable || busy"></textarea></label>
                  <label>{{ t('image') }}<textarea :value="product.image_urls.join('\n')" class="form-control" :disabled="!editable || busy" @change="setImages(product, $event.target.value)"></textarea></label>
                  <label><input v-model="product.available" type="checkbox" :disabled="!editable || busy"> {{ t('available') }}</label>
                  <label>{{ t('tax') }}<select v-model="product.vat_id" class="form-control" :disabled="!editable || busy"><option :value="null">{{ t('unknown') }}</option><option v-for="tax in taxes" :key="tax.id" :value="tax.id">{{ tax.amount }}%</option></select></label>
                  <div v-if="product.deposit">SGR: {{ product.deposit.name }} · {{ product.deposit.price }}</div>
                  <details v-for="option in product.options" :key="option.id" class="option-row">
                    <summary><input v-model="option.included" type="checkbox" :disabled="!editable || busy" @click.stop> {{ option.name }} · {{ option.min }}–{{ option.max }}</summary>
                    <input v-model="option.name" class="form-control" :disabled="!editable || busy">
                    <label>{{ t('mode') }}<select v-model="option.mode" class="form-control" :disabled="!editable || busy"><option value="single">{{ t('single') }}</option><option value="multiple">{{ t('multiple') }}</option><option value="counter">{{ t('counter') }}</option></select></label>
                    <div class="range"><label>{{ t('groupMin') }}<input v-model.number="option.min" type="number" min="0" class="form-control" :disabled="!editable || busy"></label><label>{{ t('groupMax') }}<input v-model.number="option.max" type="number" min="0" class="form-control" :disabled="!editable || busy"></label></div>
                    <div v-for="value in option.values" :key="value.id" class="value-row">
                      <input v-model="value.name" class="form-control" :disabled="!editable || busy" :aria-label="t('name')">
                      <label>{{ t('price') }}<input v-model.number="value.price" type="number" min="0" step="0.01" class="form-control" :disabled="!editable || busy"></label>
                      <div class="range"><label>{{ t('eachMin') }}<input :value="value.min_quantity" type="number" min="0" :placeholder="t('unknown')" class="form-control" :disabled="!editable || busy" @input="setQuantity(value, 'min_quantity', $event.target.value)"></label><label>{{ t('eachMax') }}<input :value="value.max_quantity" type="number" min="1" :placeholder="t('unknown')" class="form-control" :disabled="!editable || busy" @input="setQuantity(value, 'max_quantity', $event.target.value)"></label></div>
                      <label>{{ t('tax') }}<select v-model="value.vat_id" class="form-control" :disabled="!editable || busy"><option :value="null">{{ t('unknown') }}</option><option v-for="tax in taxes" :key="tax.id" :value="tax.id">{{ tax.amount }}%</option></select></label>
                      <label><input v-model="value.available" type="checkbox" :disabled="!editable || busy"> {{ t('available') }}</label>
                      <label><input v-model="value.selected_by_default" type="checkbox" :disabled="!editable || busy"> {{ t('default') }}</label>
                      <label>{{ t('image') }}<textarea :value="value.image_urls.join('\n')" class="form-control" :disabled="!editable || busy" @change="setImages(value, $event.target.value)"></textarea></label>
                      <p v-if="value.fee_label" class="text-warning">{{ value.fee_label }} · {{ t('reviewFee') }}</p>
                      <button v-if="!value.deposit && editable" type="button" class="import-button import-button--secondary" @click="$set(value, 'deposit', { name: 'SGR', price: 0, representation: 'deposit' })">{{ t('addDeposit') }}</button>
                      <div v-if="value.deposit" class="range"><label>SGR<input v-model="value.deposit.name" class="form-control" :disabled="!editable || busy"></label><label>{{ t('price') }}<input v-model.number="value.deposit.price" type="number" min="0" step="0.01" class="form-control" :disabled="!editable || busy"></label></div>
                      <label v-if="value.deposit">SGR · {{ t('tax') }}<select v-model="value.deposit.vat_id" class="form-control" :disabled="!editable || busy"><option :value="null">{{ t('unknown') }}</option><option v-for="tax in taxes" :key="tax.id" :value="tax.id">{{ tax.amount }}%</option></select></label>
                      <details><summary>{{ t('source') }}</summary><pre>{{ JSON.stringify(value.source, null, 2) }}</pre></details>
                    </div>
                  </details>
                  <details><summary>{{ t('source') }}</summary><pre>{{ JSON.stringify(product.source, null, 2) }}</pre></details>
                </div>
              </details>
            </main>
          </div>

        <footer v-if="editable" class="confirm-bar">
          <div class="confirm-checks">
            <label><input v-model="draft.acknowledged_gaps" type="checkbox" :disabled="busy"> <span>{{ t('acknowledge') }}</span></label>
            <label><input v-model="draft.skip_failed_images" type="checkbox" :disabled="busy"> <span>{{ t('skipImages') }}</span></label>
          </div>
          <p v-if="error" class="import-error" role="alert">{{ error }}</p>
          <div class="confirm-actions">
            <span v-if="!draft.acknowledged_gaps" class="confirm-hint">{{ t('acknowledgeHint') }}</span>
            <span v-else class="confirm-hint">{{ selectedCount }} {{ t('products') }}</span>
            <div class="confirm-buttons">
              <button type="button" class="import-button import-button--danger" :disabled="busy" @click="cancel">{{ t('cancel') }}</button>
              <button type="button" class="import-button import-button--secondary" :disabled="busy" @click="save(false)"><i class="fa" :class="busy && activeAction === 'save' ? 'fa-spinner fa-spin' : 'fa-save'" aria-hidden="true"></i> {{ t('save') }}</button>
              <button type="button" class="import-button import-button--primary" :disabled="busy || !draft.acknowledged_gaps || !selectedCount" @click="save(true)"><i class="fa" :class="busy && activeAction === 'confirm' ? 'fa-spinner fa-spin' : 'fa-check'" aria-hidden="true"></i> {{ t('confirm') }} ({{ selectedCount }})</button>
            </div>
          </div>
        </footer>
        <p v-if="error && !editable" class="import-error" role="alert">{{ error }}</p>
      </div>
    </div>
    <section class="import-history" :aria-label="t('history')">
      <div class="import-history__heading"><h5>{{ t('history') }}</h5><span>{{ history.length }}</span></div>
      <div class="import-history__table data-table">
        <el-table :data="history" :empty-text="t('noHistory')" :row-class-name="historyRowClass" style="width: 100%">
          <el-table-column prop="platform" :label="t('platform')" width="120" align="left" header-align="left">
            <template slot-scope="scope"><span class="history-platform">{{ scope.row.platform }}</span></template>
          </el-table-column>
          <el-table-column prop="store_url" :label="t('storeUrl')" min-width="260" align="left" header-align="left" show-overflow-tooltip />
          <el-table-column prop="created_at" :label="t('date')" width="180" align="left" header-align="left" />
          <el-table-column prop="status" :label="t('status')" width="170" align="left" header-align="left">
            <template slot-scope="scope"><span class="history-status" :class="'history-status--' + String(scope.row.status).toLowerCase()">{{ historyStatus(scope.row.status) }}</span></template>
          </el-table-column>
          <el-table-column :label="$t('general.actions')" width="130" align="center" header-align="center" class-name="history-actions" label-class-name="history-actions">
            <template slot-scope="scope">
              <el-dropdown trigger="click" placement="bottom-end" :disabled="busy" @click.native.stop>
                <span class="arrow-box">&#9662;</span>
                <el-dropdown-menu slot="dropdown" class="custom-dropdown-menu">
                  <el-dropdown-item :disabled="busy" @click.native.stop="!busy && open(scope.row.id)">{{ t('open') }}</el-dropdown-item>
                  <el-dropdown-item :disabled="busy || isImportRunning(scope.row)" @click.native.stop="historyAction(scope.row, 'reimport')">{{ t('reimport') }}</el-dropdown-item>
                  <el-dropdown-item v-if="['CONFIRMED', 'PRODUCTS_DELETED'].indexOf(scope.row.status) !== -1" :disabled="busy" divided @click.native.stop="historyAction(scope.row, 'deleteProducts')">{{ t(scope.row.status === 'PRODUCTS_DELETED' ? 'cleanupImport' : 'deleteProducts') }}</el-dropdown-item>
                  <el-dropdown-item :disabled="busy || isImportRunning(scope.row)" divided @click.native.stop="historyAction(scope.row, 'deleteImport')">{{ t('deleteImport') }}</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </section>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Dropdown, DropdownMenu, DropdownItem } from 'element-ui'
import swal from 'sweetalert2'
import WoltMenuLayout from '../MenuPreview/WoltMenuLayout.vue'
import GlovoMenuLayout from '../MenuPreview/GlovoMenuLayout.vue'
import BoltMenuLayout from '../MenuPreview/BoltMenuLayout.vue'
export default {
  name: 'PlatformMenuImports',
  components: { WoltMenuLayout, GlovoMenuLayout, BoltMenuLayout, [Table.name]: Table, [TableColumn.name]: TableColumn, [Dropdown.name]: Dropdown, [DropdownMenu.name]: DropdownMenu, [DropdownItem.name]: DropdownItem },
  props: { route: { type: Object, default: () => ({}) } },
  data () { return { reviewMode: 'preview', activeAction: '', storeUrl: '', batch: null, draft: null, history: [], taxes: [], busy: false, error: '', search: '', timer: null, openSequence: 0 } },
  computed: {
    locationId () { return ((this.route && this.route.params) || this.$route.params).locationId },
    editable () { return this.batch && this.batch.status === 'READY' },
    processing () { return this.batch && ['PROCESSING', 'CONFIRMING'].indexOf(this.batch.status) !== -1 },
    diagnostics () { return this.batch ? (this.batch.diagnostics || []).filter(issue => issue.code !== 'CATEGORY_IMAGE_ABSENT') : [] },
    selectedCount () { return this.draft ? this.draft.products.filter(p => p.included).length : 0 },
    resultCount () { return Object.keys((this.batch && this.batch.result && this.batch.result.products) || {}).length },
    previewLayout () { return { wolt: 'WoltMenuLayout', glovo: 'GlovoMenuLayout', bolt: 'BoltMenuLayout' }[(this.batch && this.batch.platform) || 'wolt'] || 'WoltMenuLayout' },
    previewCategories () {
      if (!this.draft) return []
      const products = {}
      this.filteredProducts.filter(product => product.included).forEach(product => {
        products[String(product.id)] = Object.assign({}, product, {
          image_url: (product.image_urls || [])[0] || '',
          options: product.options.filter(option => option.included !== false).map(option => Object.assign({}, option, {
            multiple: option.mode === 'multiple', counter: option.mode === 'counter'
          }))
        })
      })
      return this.draft.categories.filter(category => category.included).map(category => Object.assign({}, category, {
        products: category.product_ids.map(id => products[String(id)]).filter(Boolean)
      })).filter(category => category.products.length)
    },
    filteredProducts () { return this.draft.products.filter(p => !this.search || JSON.stringify(p).toLowerCase().indexOf(this.search.toLowerCase()) !== -1) }
  },
  created () {
    this.loadHistory()
    const params = (this.route && this.route.params) || this.$route.params
    if (params.importId) this.open(params.importId)
  },
  beforeDestroy () { clearTimeout(this.timer) },
  methods: {
    isImportRunning (item) { return ['PROCESSING', 'CONFIRMING'].indexOf(item.status) !== -1 },
    async historyAction (item, action) {
      if (this.busy || this.isImportRunning(item)) return
      if (action !== 'reimport') {
        try {
          await swal({
            title: this.t(action === 'deleteProducts' && item.status === 'PRODUCTS_DELETED' ? 'cleanupImport' : action),
            text: this.t(action === 'deleteProducts' ? (item.status === 'PRODUCTS_DELETED' ? 'cleanupImportConfirm' : 'deleteProductsConfirm') : 'deleteImportConfirm'),
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: this.$t('general.delete'),
            cancelButtonText: this.$t('general.cancel')
          })
        } catch (e) { return }
      }
      if (this.busy) return
      this.busy = true
      this.error = ''
      const suffix = action === 'reimport' ? '/reimport' : (action === 'deleteProducts' ? '/products' : '')
      this.request('/' + item.id + suffix, action === 'reimport' ? 'POST' : 'DELETE', {}, response => {
        this.busy = false
        if (action === 'reimport') {
          this.storeUrl = response.data.store_url
          this.open(response.data.id)
        } else if (this.batch && this.batch.id === item.id) {
          if (action === 'deleteImport') { this.batch = null; this.draft = null; clearTimeout(this.timer) } else { this.open(item.id) }
        }
        this.loadHistory()
      })
    },
    warningMessage (issue) {
      const codes = ['DEFAULT_MODIFIER_PRICE_SEPARATED', 'INDIVIDUAL_LIMITS_ABSENT', 'MISSING_TAX_AND_POS_MAPPING', 'ADDITIONAL_SOURCE_DETAILS', 'PRODUCT_DEPOSIT_AS_REQUIRED_GROUP', 'IMAGE_DOWNLOAD_FAILED', 'DEPOSIT_FROM_FEE_LABEL', 'FEE_REQUIRES_REVIEW', 'UNSUPPORTED_OPTION_PRICING_OR_DEPENDENCY']
      if (codes.indexOf(issue.code) === -1) return issue.message
      const parameters = Object.assign({}, issue.parameters || {})
      // Older saved previews contain dynamic values only in the original message.
      if (issue.code === 'DEFAULT_MODIFIER_PRICE_SEPARATED') {
        const match = String(issue.message || '').match(/total \(([^)]+)\)/)
        const amount = parameters.amount != null ? parameters.amount : match && match[1]
        parameters.amount = amount == null ? '—' : Number(amount).toLocaleString(this.$i18n.locale)
      }
      if (issue.code === 'DEPOSIT_FROM_FEE_LABEL' || issue.code === 'FEE_REQUIRES_REVIEW') {
        const match = String(issue.message || '').match(/: (.*)$/)
        parameters.label = parameters.label || (match && match[1]) || ''
      }
      return this.$t('platformMenuImport.warnings.' + issue.code, parameters)
    },
    goBack () {
      if (!this.busy) this.$emit('close')
    },
    validateStoreUrl (input) {
      input.setCustomValidity('')
      if (input.validity.valueMissing) input.setCustomValidity(this.t('storeUrlRequired'))
      else if (input.validity.typeMismatch) input.setCustomValidity(this.t('storeUrlInvalid'))
    },
    historyStatus (status) {
      const key = { PROCESSING: 'statusProcessing', READY: 'statusReady', CONFIRMING: 'statusConfirming', CONFIRMED: 'statusConfirmed', PRODUCTS_DELETED: 'statusProductsDeleted', FAILED: 'statusFailed', CANCELED: 'statusCanceled' }[status]
      return key ? this.t(key) : status
    },
    historyRowClass ({ row }) { return this.batch && row.id === this.batch.id ? 'history-current' : '' },
    t (key) { return this.$t('platformMenuImport.' + key) },
    request (path, method, data, done) {
      this.apiRequest('/platform-menu-imports' + path, method, Object.assign({ token: this.$session.get('token'), company_id: this.$session.get('selectedCompany') }, data), response => {
        if (!response || !response.status) {
          const failure = response && response.error
          const http = failure && failure.response
          const payload = (http && http.data) || response || {}
          const validation = payload.errors && Object.keys(payload.errors).map(key => [].concat(payload.errors[key]).join(' ')).join(' ')
          this.error = http && http.status === 404 ? this.t('apiUnavailable')
            : payload.message || validation || (typeof payload.data === 'string' && payload.data) ||
              (http && http.status === 403 ? this.t('accessDenied')
                : failure && failure.code === 'ECONNABORTED' ? this.t('timedOut') : this.t('requestFailed'))
          this.busy = false
          return
        }
        done(response)
      }, { timeout: 600000 })
    },
    loadHistory () { this.request('', 'GET', { location_id: this.locationId }, r => { this.history = r.data.data || [] }) },
    createImport () {
      if (this.busy || !this.storeUrl.trim()) return
      this.activeAction = 'preview'
      this.busy = true
      this.error = ''
      this.request('', 'POST', { store_url: this.storeUrl, location_id: this.locationId }, r => { this.busy = false; this.open(r.data.id); this.loadHistory() })
    },
    open (id) {
      clearTimeout(this.timer)
      const sequence = ++this.openSequence
      this.error = ''
      this.request('/' + id, 'GET', {}, r => {
        if (sequence !== this.openSequence) return
        this.batch = r.data
        this.reviewMode = 'preview'
        this.taxes = r.tax_options || []
        this.draft = r.data.draft ? JSON.parse(JSON.stringify(r.data.draft)) : null
        if (this.draft) this.draft.products.forEach(p => p.options.forEach(o => { if (o.included === undefined) this.$set(o, 'included', true) }))
        if (this.processing) this.timer = setTimeout(() => this.open(id), 2500)
      })
    },
    setImage (entity, value) { entity.image_urls = value.trim() ? [value.trim()] : [] },
    setImages (entity, value) { entity.image_urls = value.split('\n').map(x => x.trim()).filter(Boolean) },
    setQuantity (value, key, input) { value[key] = input === '' ? null : Number(input) },
    categoryChanged (changed) {
      const byId = {}
      this.draft.categories.forEach(c => { byId[c.id] = c })
      if (changed.included) {
        let parent = byId[changed.parent_id]
        const visited = new Set()
        while (parent && !visited.has(parent.id)) {
          visited.add(parent.id)
          parent.included = true
          parent = byId[parent.parent_id]
        }
      } else {
        const excluded = new Set([changed.id])
        this.draft.categories.forEach(() => this.draft.categories.forEach(c => {
          if (excluded.has(c.parent_id)) { c.included = false; excluded.add(c.id) }
        }))
      }
      const ids = new Set()
      this.draft.categories.filter(c => c.included).forEach(c => c.product_ids.forEach(id => ids.add(String(id))))
      this.draft.products.forEach(p => { p.included = ids.has(String(p.id)) })
    },
    productChanged (product) {
      if (product.included && !this.draft.categories.some(c => c.included && c.product_ids.indexOf(product.id) !== -1)) {
        const category = this.draft.categories.find(c => c.product_ids.indexOf(product.id) !== -1)
        if (category) category.included = true
      }
    },
    save (confirm) {
      if (this.busy || !this.editable || (confirm && (!this.draft.acknowledged_gaps || !this.selectedCount))) return
      this.activeAction = confirm ? 'confirm' : 'save'
      this.busy = true
      this.error = ''
      this.request('/' + this.batch.id, 'PUT', { revision: this.batch.revision, draft: this.draft }, r => {
        this.batch = r.data
        if (!confirm) { this.busy = false; return }
        this.request('/' + this.batch.id + '/confirm', 'POST', { revision: this.batch.revision }, response => {
          this.busy = false
          this.batch = response.data
          this.open(this.batch.id)
          this.loadHistory()
        })
      })
    },
    cancel () {
      if (this.busy || !this.editable) return
      this.activeAction = 'cancel'
      this.busy = true
      this.error = ''
      this.request('/' + this.batch.id + '/cancel', 'POST', {}, () => {
        this.busy = false
        this.open(this.batch.id)
        this.loadHistory()
      })
    }
  }
}
</script>

<style scoped>
.platform-import { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; overflow: visible; color: #243653; }
.import-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; padding: 20px 22px 12px; border-bottom: 1px solid #e5e7eb; }
.import-header h4 { margin: 0 0 6px; font-size: 20px; font-weight: 700; }
.import-header p { margin: 0; max-width: 920px; color: #6b7280; font-size: 13px; line-height: 1.6; }
.platform-import .import-button { display: inline-flex; flex: 0 0 auto; align-items: center; justify-content: center; gap: 8px; width: auto !important; height: auto !important; min-height: 40px; min-width: 100px; padding: 10px 18px !important; margin: 0 !important; border: 1px solid transparent; border-radius: 50px; font-family: inherit; font-size: 13px; font-weight: 600; line-height: 1.4; text-transform: none !important; white-space: normal; text-align: center; cursor: pointer; box-shadow: none; }
.platform-import .import-button--primary { background: #20bfab; border-color: #20bfab; color: #fff; }
.platform-import .import-button--secondary { background: #f2eeff; border-color: #6846d1; color: #5935c3; }
.platform-import .import-button--danger { background: #ffebea; border-color: #ffebea; color: #e43340; }
.platform-import .import-button:hover:not(:disabled) { filter: brightness(.96); }
.platform-import .import-button:focus-visible, .review-tabs button:focus-visible { outline: 3px solid #81d8cd; outline-offset: 3px; }
.platform-import .import-button:disabled { background: #eef1f4; border-color: #dfe3e8; color: #87919e; cursor: not-allowed; opacity: 1; }
.url-form { display: flex; gap: 12px; align-items: center; padding: 16px 22px; margin: 0; }
.url-form input, .review-bar input { flex: 1; min-width: 0; height: 40px; background: #fff; border: 1px solid #d1d5db; border-radius: 10px; }
.import-summary { display: flex; gap: 12px; align-items: center; padding: 0 22px 12px; font-size: 12px; color: #6b7280; }
.import-status { background: #edf7f5; color: #128475; padding: 5px 10px; border-radius: 20px; font-weight: 600; text-transform: capitalize; }
.diagnostics { margin: 0 22px 12px; background: #fffbeb; border: 1px solid #f3e5b3; border-radius: 10px; padding: 12px 16px; color: #86601e; font-size: 13px; }
.diagnostics ul { margin: 10px 0 0; padding-left: 20px; line-height: 1.7; }
.review-bar { display: flex; gap: 14px; align-items: center; padding: 12px 18px; border-top: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb; background: #fafafa; }
.review-tabs { display: flex; flex: 0 0 auto; gap: 4px; padding: 4px; background: #eaf0f3; border-radius: 10px; }
.review-tabs button { border: 0; border-radius: 7px; padding: 8px 12px; font: inherit; font-size: 13px; color: #64748b; background: transparent; cursor: pointer; }
.review-tabs button.is-active { background: #fff; color: #128475; box-shadow: 0 1px 4px #0000000f; font-weight: 600; }
.review-count { color: #6b7280; font-size: 12px; white-space: nowrap; }
.preview-stage { height: 60vh; min-height: 400px; max-height: 720px; overflow: hidden; background: #f3f4f6; }
.preview-stage > * { height: 100%; }
.preview-empty { display: flex; align-items: center; justify-content: center; color: #6b7280; }
.review-columns { display: grid; grid-template-columns: 240px minmax(0, 1fr); gap: 20px; padding: 20px; max-height: 65vh; overflow: auto; background: #f3f4f6; }
.category-row, .product-row, .option-row, .value-row { border: 1px solid #e5e7eb; background: #fff; border-radius: 12px; padding: 14px; margin-bottom: 12px; }
.category-row img { width: 70px; max-height: 55px; object-fit: cover; border-radius: 8px; }
.product-editor > img { width: 160px; max-height: 120px; object-fit: cover; border-radius: 10px; }
.product-editor { padding-top: 16px; }
.review-columns label { display: block; font-size: 13px; margin: 10px 0; }
.review-columns .form-control { margin-top: 5px; background: #fff; border: 1px solid #d1d5db; border-radius: 8px; }
.review-columns textarea { min-height: 70px; resize: vertical; }
.range { display: flex; gap: 12px; align-items: center; }
.range label { flex: 1; min-width: 0; }
summary { cursor: pointer; font-weight: 600; }
pre { white-space: pre-wrap; overflow-wrap: anywhere; max-height: 250px; overflow: auto; }
.confirm-bar { position: sticky; bottom: 0; z-index: 10; background: #fff; border-top: 1px solid #e5e7eb; box-shadow: 0 -4px 16px #00000005; padding: 16px 22px; border-radius: 0 0 14px 14px; }
.confirm-checks { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
.confirm-checks label { display: flex; align-items: flex-start; gap: 9px; font-size: 13px; line-height: 1.5; margin: 0; cursor: pointer; }
.platform-import input[type=checkbox] { width: 16px; height: 16px; flex-shrink: 0; margin: 2px 0 0; accent-color: #20bfab; }
.confirm-actions { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
.confirm-hint { color: #6b7280; font-size: 12px; }
.confirm-buttons { display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: 10px; }
.import-error, .import-success, .import-progress { margin: 12px 22px; padding: 12px 16px; border-radius: 10px; line-height: 1.5; }
.import-error { background: #ffebea; color: #b42330; }
.confirm-bar .import-error { margin: 0 0 12px; }
.import-success { background: #e8f7f0; color: #128475; }
.import-progress { background: #f0f7fa; color: #475569; }
.platform-import-page > .settings-top-bar { margin-bottom: 16px; }
.import-history { border-top: 1px solid #e5e7eb; padding: 20px 22px; }
.import-history__heading { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.import-history__heading h5 { margin: 0; font-size: 15px; font-weight: 600; }
.import-history__heading > span { padding: 3px 9px; border-radius: 20px; background: #eef1fa; color: #64748b; font-size: 12px; }
.history-platform { text-transform: capitalize; font-weight: 600; }
.history-status { display: inline-block; padding: 5px 10px; border-radius: 20px; background: #eef1f4; color: #64748b; font-size: 12px; line-height: 1.4; }
.history-status--confirmed { background: #e8f7f0; color: #128475; }
.history-status--ready { background: #edf4ff; color: #3564b1; }
.history-status--processing, .history-status--confirming { background: #fff6dc; color: #996c11; }
.history-status--failed { background: #ffebea; color: #b42330; }
.history-status--products_deleted { background: #fff6dc; color: #996c11; }
.arrow-box { display: inline-block; font-size: 18px; padding: 4px 6px; border: 1px solid #674bd2 !important; border-radius: 4px; cursor: pointer; text-align: center; user-select: none; color: #674bd2; }
.arrow-box:hover { background: #674bd2 !important; color: #fff !important; }
.custom-dropdown-menu { min-width: 180px; font-size: 14px; }

.import-history__table >>> .el-table { background: #fff; }
.import-history__table >>> .el-table thead th { background: #e4e8f9 !important; padding: 8px 0 !important; border-bottom: none !important; }
.import-history__table >>> .el-table thead th:first-child { border-radius: 11px 0 0 11px; }
.import-history__table >>> .el-table thead th:last-child { border-radius: 0 11px 11px 0; }
.import-history__table >>> .el-table th > .cell { background: transparent !important; height: auto !important; min-height: 32px; padding: 4px 14px; line-height: 1.4; }
.import-history__table >>> .el-table td { background: #fff !important; border-bottom: 1px solid #e6effb !important; padding: 10px 0 !important; }
.import-history__table >>> .el-table td > .cell { background: transparent !important; height: auto !important; min-height: 34px; padding: 4px 14px; line-height: 1.4; border-radius: 0 !important; }
.import-history__table >>> .el-table .history-current > td, .import-history__table >>> .el-table tbody tr:hover > td { background: #f5f7fe !important; }
.import-history__table >>> .el-table tr { clip-path: none; }
.import-history__table >>> .el-table th > .cell, .import-history__table >>> .el-table td > .cell { display: block; box-sizing: border-box; text-align: left; }
.import-history__table >>> .el-table .history-actions > .cell { display: flex; align-items: center; justify-content: center; text-align: center; }
.import-history__table >>> .el-table .history-actions .btn { margin: 0; }

@media (max-width: 900px) { .review-bar { flex-wrap: wrap; } .review-count { width: 100%; } .confirm-actions { align-items: stretch; flex-direction: column; } .confirm-buttons { justify-content: flex-start; } }
@media (max-width: 600px) { .import-header { flex-direction: column; padding: 16px; } .url-form { flex-direction: column; align-items: stretch; padding: 16px; } .review-columns { grid-template-columns: 1fr; padding: 12px; } .review-tabs { width: 100%; } .review-tabs button { flex: 1; } .confirm-bar { padding: 14px; } .confirm-buttons { flex-direction: column; align-items: stretch; } .confirm-buttons .import-button { width: 100% !important; } }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Integrations/PlatformMenuImports/PlatformMenuImports.vue